// ============================================================================
// Idempotencia para operaciones fiscales críticas
// ============================================================================
// Las operaciones de emisión, envío, firma y descuento de inventario deben ser
// idempotentes: repetir una operación (por doble clic, reintento de red, etc.)
// NO debe producir duplicados (doble factura, doble e-NCF, doble envío, doble
// descuento de inventario).
//
// Cada operación crítica lleva un `idempotency_key` único. El backend la usa
// para detectar y descartar peticiones repetidas de forma segura.
// ============================================================================

/** Genera una clave de idempotencia aleatoria (UUID v4). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback para entornos sin crypto.randomUUID.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Genera una clave de idempotencia determinista a partir de un ámbito y partes
 * estables. Útil para operaciones que se pueden repetir con los mismos
 * parámetros y deben mapear al mismo resultado (p. ej. envío de un documento
 * ya firmado).
 *
 * @param scope Ámbito de la operación (p. ej. "send", "sign", "reserve-e-ncf").
 * @param parts Partes estables que identifican la operación (ids, e-NCF, etc.).
 */
export function idempotencyKeyFor(
  scope: string,
  ...parts: Array<string | number>
): string {
  return [scope, ...parts.map((p) => String(p))].join(':');
}

/** Valida que una clave de idempotencia sea una cadena no vacía. */
export function isValidIdempotencyKey(key: unknown): key is string {
  return typeof key === 'string' && key.trim().length > 0;
}